import { useState, useEffect, useRef } from 'react';
import { api } from '../../api/client';
import { useApi } from '../../hooks/useApi';
import { Btn, Input, Select, Modal, Spinner, useToast } from '../ui';
import { AssigneePicker } from './AssigneePicker';
import { InlineSearchPicker, EDITABLE_STATUSES, useDealPickerItems, dealPickerValueFor } from '../tasks/TaskComponents';
import { scopeFromPick } from './DealScopePicker';

// Shared task editor modal — identical layout everywhere (Tasks table, Dashboard, …).
// Self-fetches the full task (for prefill) plus the deal/parent pickers so callers
// only need to pass the taskId and a couple of callbacks.
//
// Props:
//   taskId    — task to edit
//   users     — user list for the assignee picker
//   onClose   — close without changes
//   onSaved   — called after a successful save or delete (caller reloads its data)
export const TaskEditModal = ({ taskId, users = [], onClose, onSaved }) => {
  const { toast } = useToast();
  const { data: task, loading } = useApi(() => api.get(`tasks/${taskId}`), [taskId]);
  const { data: contacts } = useApi(() => api.get('contacts'), []);
  const { data: allTasks } = useApi(() => api.get('tasks'), []);

  // Deal / Deal Detail picker data — shared with the Email Inbox picker.
  // Projects are rows in the same list: one pick sets deal, detail and project.
  const { dealPickerOptions, dealPickerItems } = useDealPickerItems({ includeProjects: true });
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [assigneesTouched, setAssigneesTouched] = useState(false);
  const [assignorsTouched, setAssignorsTouched] = useState(false);
  const [outsidePartiesTouched, setOutsidePartiesTouched] = useState(false);
  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));
  // Which task the deal combo has been seeded for. The combo is filled ONCE per
  // task, never "whenever it is empty": typing in the picker empties it, and an
  // is-empty trigger snapped the old deal straight back (feedback #581).
  const comboSeededFor = useRef(null);

  // Prefill the form once the task loads.
  useEffect(() => {
    if (!task) return;
    const assignees = (task.assignees || []).map(a =>
      a.userID
        ? { type: 'user', id: a.userID, name: a.displayName || '' }
        : { type: 'contact', id: a.contactDetailID, name: a.contactName || '' }
    ).filter(a => a.id);
    // Assignors are always users. Fall back to the singular assignedBy* fields
    // for tasks fetched before the multi-assignor migration backfilled them.
    const assignors = ((task.assignors && task.assignors.length)
      ? task.assignors.map(a => ({ type: 'user', id: a.userID, name: a.displayName || '' }))
      : (task.assignedByUserID ? [{ type: 'user', id: task.assignedByUserID, name: task.assignedByName || '' }] : [])
    ).filter(a => a.id);
    // Outside parties are always contacts (ContactDetails people).
    const outsideParties = (task.outsideParties || [])
      .map(p => ({ type: 'contact', id: p.contactDetailID, name: p.contactName || '' }))
      .filter(p => p.id);
    setForm({
      title:        task.title        || '',
      priority:     task.priority     || 'Medium',
      status:       task.status       || 'Not Started',
      dueDate:      task.dueDate ? task.dueDate.split('T')[0] : '',
      parentTaskId: task.parentTaskID || '',
      dealId:       task.dealID       || '',
      dealDetailId: task.dealDetailID || '',
      projectId:    task.projectID    || '',
      dealCombo:    '',
      assignors,
      assignees,
      outsideParties,
    });
  }, [task]);

  // Derive the deal-combo value from the task once both it and the picker
  // options exist. The picker lists every deal detail, so a task on a detail
  // always resolves to that detail's row — it used to be coerced back to the
  // deal-level row whenever the deal exposed only one detail, which silently
  // re-targeted the task to deal level on the next save.
  useEffect(() => {
    if (!form || !task) return;
    if (comboSeededFor.current === taskId) return;
    const dealId = task.dealID || '';
    const detailId = task.dealDetailID || '';
    if (!dealId) { comboSeededFor.current = taskId; return; }
    // Wait for the options so a detail row exists to select before we commit to
    // a combo. It is seeded once per task, then left to the person.
    if ((detailId || task.projectID) && dealPickerOptions.length === 0) return;
    comboSeededFor.current = taskId;
    // A project-tagged task opens on its project row — but only if that row is
    // offered (a deleted project falls back to the task's detail/deal row).
    const projectId = task.projectID && dealPickerItems.some(i => i.projectID && i.value === `project:${task.projectID}`)
      ? task.projectID : '';
    setForm(p => ({ ...p, dealCombo: dealPickerValueFor({ dealId, dealDetailId: detailId, projectId }) }));
  }, [form, task, taskId, dealPickerOptions, dealPickerItems]);

  const setDealCombo = (combo) => {
    const scope = scopeFromPick(combo, dealPickerItems);
    setForm(p => ({ ...p, dealCombo: combo || '', dealId: scope.dealId, dealDetailId: scope.dealDetailId, projectId: scope.projectId }));
  };

  // Did the user re-target the task to a different deal / deal detail? Moving
  // is a distinct operation (it carries subtasks and can clear the detail), so
  // it goes through the dedicated move endpoint rather than the scalar PUT.
  const dealChanged = !!task && (
    (form?.dealId || '') !== (task.dealID || '') ||
    (form?.dealDetailId || '') !== (task.dealDetailID || '')
  );
  const childCount = (task?.children || []).length;

  const save = async () => {
    if (!form.title?.trim()) { toast.error('Title is required'); return; }
    if (form.assignees.length === 0) { toast.error('Assign the task to at least one person'); return; }
    if (form.assignors.length === 0) { toast.error('The task must be assigned by at least one person'); return; }
    setSaving(true);
    try {
      // Deal / detail are intentionally omitted here — a move re-homes the whole
      // subtask tree and can move a task up to deal-level, which the COALESCE
      // update on this endpoint can't do. Handled by the move call below.
      const payload = {
        title:        form.title.trim(),
        priority:     form.priority || 'Medium',
        status:       form.status   || 'Not Started',
        dueDate:      form.dueDate       || null,
        parentTaskId: form.parentTaskId  || null,
      };
      // The project tag, chosen in the deal list. Sent only when it changed and a
      // deal is still selected ("— No deal —" leaves the task where it is).
      if (form.dealId && (form.projectId || '') !== (task.projectID || '')) {
        payload.projectId = form.projectId || null;
      }
      // Current (reassignable) assignors — a task can be assigned by multiple
      // people. The original assignor is tracked server-side and never sent
      // from here. Only touch assignors when loaded or edited, so a normal edit
      // can't wipe them if the prefill ever came back empty.
      if (assignorsTouched || form.assignors.length > 0) {
        payload.assignedByUserIds = form.assignors.filter(a => a.type === 'user').map(a => a.id);
      }
      // Only touch assignees when we actually have them loaded (or the user
      // edited the picker). Otherwise omit them so a normal edit can't wipe
      // existing assignees if the prefill ever came back empty.
      if (assigneesTouched || form.assignees.length > 0) {
        payload.assigneeIds        = form.assignees.filter(a => a.type === 'user').map(a => a.id);
        payload.contactAssigneeIds = form.assignees.filter(a => a.type === 'contact').map(a => a.id);
      }
      // Outside parties (optional). Only send when the picker was loaded with
      // parties or the user edited it, so a normal edit can't silently clear a
      // prefill that came back empty.
      if (outsidePartiesTouched || form.outsideParties.length > 0) {
        payload.outsidePartyIds = form.outsideParties.map(a => a.id);
      }
      await api.put(`tasks/${taskId}`, payload);
      // Move the task (and its whole subtask tree) to the new deal / detail.
      // Only when a real deal is selected — a task's DealID is NOT NULL, so
      // "— No deal —" leaves the current deal untouched.
      if (form.dealId && dealChanged) {
        await api.put(`tasks/${taskId}/move`, {
          dealId:       form.dealId,
          dealDetailId: form.dealDetailId || null,
        });
      }
      onSaved?.();
      onClose?.();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${task?.title}"? This cannot be undone from the UI.`)) return;
    setSaving(true);
    try {
      await api.del(`tasks/${taskId}`);
      onSaved?.();
      onClose?.();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={task ? `Edit — ${task.title}` : 'Edit Task'} onClose={onClose} width={600}>
      {(loading || !form) ? <Spinner /> : (
        <>
          <Input autoFocus label="Title" value={form.title} onChange={v => setF('title', v)} required placeholder="Task title…" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Select label="Priority" value={form.priority} onChange={v => setF('priority', v)} options={['Low','Medium','High','Critical']} />
            <Select label="Status"   value={form.status}   onChange={v => setF('status', v)}   options={EDITABLE_STATUSES.map(s => ({ value: s, label: s }))} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Input label="Due Date" type="date" value={form.dueDate} onChange={v => setF('dueDate', v)} />
            <InlineSearchPicker label="Parent Task (optional)" value={form.parentTaskId}
              onChange={v => setF('parentTaskId', v || '')}
              items={(allTasks || []).filter(t => !t.parentTaskID && t.taskID !== taskId).map(t => ({ value: t.taskID, label: t.title }))}
              placeholder="Search tasks…" clearLabel="— None (top-level task) —" />
          </div>
          <InlineSearchPicker label="Deal / Deal Detail / Project (optional)" value={form.dealCombo}
            onChange={setDealCombo}
            items={dealPickerItems} placeholder="Search deals, details or projects…" clearLabel="— No deal —" />
          {!form.dealId && task?.dealID && (
            <div style={{ marginTop: -8, marginBottom: 12, fontSize: 12, color: '#6b7280' }}>
              Every task belongs to a deal, so "No deal" keeps this task on its current deal. Pick another deal to move it.
            </div>
          )}
          {dealChanged && form.dealId && childCount > 0 && (
            <div style={{ marginTop: -8, marginBottom: 12, fontSize: 12, color: '#6b7280' }}>
              Moving this task will also move its {childCount} subtask{childCount === 1 ? '' : 's'}.
            </div>
          )}
          <AssigneePicker label="Assigned By" searchPlaceholder="Search people who assigned this…"
            selected={form.assignors} onChange={v => { setAssignorsTouched(true); setF('assignors', v); }}
            users={users || []} contacts={[]} required />
          {task?.originalAssignedByUserID && !form.assignors.some(a => a.id === task.originalAssignedByUserID) && task.originalAssignedByName && (
            <div style={{ marginTop: -8, marginBottom: 12, fontSize: 12, color: '#6b7280' }}>
              Originally assigned by {task.originalAssignedByName}
            </div>
          )}
          <AssigneePicker selected={form.assignees} onChange={v => { setAssigneesTouched(true); setF('assignees', v); }} users={users || []} contacts={contacts || []} required />
          <AssigneePicker label="Outside Parties" searchPlaceholder="Search contacts to add…"
            selected={form.outsideParties} onChange={v => { setOutsidePartiesTouched(true); setF('outsideParties', v); }}
            users={[]} contacts={contacts || []} />
          <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
            <Btn variant="danger" disabled={saving} onClick={remove}>Delete</Btn>
            <div style={{ display: 'flex', gap: 10 }}>
              <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
              <Btn disabled={!form.title?.trim() || form.assignees.length === 0 || form.assignors.length === 0 || saving} onClick={save}>{saving ? 'Saving…' : 'Save'}</Btn>
            </div>
          </div>
        </>
      )}
    </Modal>
  );
};
